/**
 * ProductionTab — Phase 7e.
 *
 * End-to-end manufacturing workflow for QSR / cafe brands:
 *   1. Pick a "production" recipe (must have yields_qty + yields_uom + sku_code).
 *   2. Enter batch multiplier + optional overhead cost.
 *   3. Allocate the batch into packed_outputs (containers × capacity).
 *   4. Backend validates stock, deducts RM + packaging, generates batch numbers,
 *      returns per-container cost + ZPL labels for thermal printing.
 *   5. Lots table shows every finished-goods lot with FIFO status + expiry
 *      colour-coding + re-print ZPL label buttons.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Factory,
    Plus,
    Trash2,
    Play,
    Printer,
    QrCode,
    AlertTriangle,
    CircleCheck,
    Package as PackageIcon,
    FileDown,
    Layers,
    RefreshCw,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { api, fmtMoney } from '../../lib/api';


export function ProductionTab() {
    const [subTab, setSubTab] = useState(() => {
        try { return localStorage.getItem('emb.production.tab') || 'runs'; }
        catch { return 'runs'; }
    });
    const change = (v) => {
        setSubTab(v);
        try { localStorage.setItem('emb.production.tab', v); } catch { /* noop */ }
    };
    return (
        <div data-testid="production-tab" className="space-y-4">
            <div>
                <h3 className="font-heading text-lg flex items-center gap-2">
                    <Factory size={18} className="text-primary" /> Production &amp; Barcode Lots
                </h3>
                <p className="text-xs text-muted-foreground max-w-2xl">
                    Manufacturing workflow for finished goods — run production, deduct raw
                    materials + packaging automatically, get traceable batch numbers and
                    printable ZPL labels with FSSAI &amp; barcode.
                </p>
            </div>
            <Tabs value={subTab} onValueChange={change}>
                <TabsList className="grid grid-cols-2 max-w-sm">
                    <TabsTrigger value="runs" data-testid="production-tab-runs">
                        <Play size={13} className="mr-1.5" /> Runs
                    </TabsTrigger>
                    <TabsTrigger value="lots" data-testid="production-tab-lots">
                        <Layers size={13} className="mr-1.5" /> Finished Goods Lots
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="runs" className="mt-4"><RunsTab /></TabsContent>
                <TabsContent value="lots" className="mt-4"><LotsTab /></TabsContent>
            </Tabs>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
 * RUNS TAB — history + New Run wizard
 * ═════════════════════════════════════════════════════════════ */
function RunsTab() {
    const [runs, setRuns] = useState(null);
    const [showWizard, setShowWizard] = useState(false);
    const [lastLabels, setLastLabels] = useState(null);

    const load = useCallback(async () => {
        setRuns(null);
        try {
            const r = await api.listProductionRuns({ limit: 60 });
            setRuns(r.items || []);
        } catch { setRuns([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                    {runs === null ? 'Loading…' : `${runs.length} recent run${runs.length === 1 ? '' : 's'}`}
                </div>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={load} className="gap-1" data-testid="production-refresh">
                        <RefreshCw size={13} /> Refresh
                    </Button>
                    <Button size="sm" className="gap-1" onClick={() => setShowWizard(true)} data-testid="production-new-run">
                        <Plus size={13} /> New production run
                    </Button>
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {runs === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : runs.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                            <Factory size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No production runs yet</div>
                        <div className="text-sm text-muted-foreground max-w-md mx-auto">
                            Set up a <b>production recipe</b> first (Recipes tab → convert a recipe),
                            then click <b>New production run</b> to pack finished goods.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">When</th>
                                    <th className="text-left px-4 py-2 font-medium">Recipe · SKU</th>
                                    <th className="text-right px-4 py-2 font-medium">Batches</th>
                                    <th className="text-right px-4 py-2 font-medium">Total yield</th>
                                    <th className="text-right px-4 py-2 font-medium">RM cost</th>
                                    <th className="text-right px-4 py-2 font-medium">Pkg cost</th>
                                    <th className="text-right px-4 py-2 font-medium">Overhead</th>
                                    <th className="text-right px-4 py-2 font-medium">Batch total</th>
                                    <th className="text-right px-4 py-2 font-medium">Lots</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r, i) => (
                                    <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`production-run-row-${i}`}>
                                        <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                            {new Date(r.at || r.created_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="font-medium">{r.recipe_name}</div>
                                            <div className="text-[10px] uppercase text-muted-foreground">{r.sku_code}</div>
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">×{r.batch_multiplier}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">
                                            {r.total_yield} <span className="text-[10px] text-muted-foreground">{r.yields_uom}</span>
                                        </td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(r.total_rm_cost)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(r.total_packaging_cost)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(r.overhead_cost)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono font-semibold">{fmtMoney(r.total_batch_cost)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{(r.lot_ids || []).length}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {showWizard && (
                <NewRunWizard
                    onClose={() => setShowWizard(false)}
                    onSubmitted={(res) => {
                        setLastLabels(res);
                        setShowWizard(false);
                        load();
                    }}
                />
            )}

            {lastLabels && (
                <LabelPreviewDialog result={lastLabels} onClose={() => setLastLabels(null)} />
            )}
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════
 * NEW RUN WIZARD
 * ═════════════════════════════════════════════════════════════ */
function NewRunWizard({ onClose, onSubmitted }) {
    const [recipes, setRecipes] = useState([]);
    const [loadingRecipes, setLoadingRecipes] = useState(true);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [recipeId, setRecipeId] = useState('');
    const [batchMult, setBatchMult] = useState('1');
    const [overhead, setOverhead] = useState('0');
    const [outputs, setOutputs] = useState([
        { product_id: '', product_name: '', container_capacity: '', packaging_item_id: '', quantity_packed: '' },
    ]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            setLoadingRecipes(true);
            try {
                const [r, mats] = await Promise.all([
                    api.listRecipes(),
                    api.stockRawMaterials(),
                ]);
                setRecipes((r.items || []).filter((x) => x.recipe_type === 'production'));
                setRawMaterials(mats.items || []);
            } finally {
                setLoadingRecipes(false);
            }
        })();
    }, []);

    const recipe = useMemo(() => recipes.find((r) => r.id === recipeId), [recipes, recipeId]);
    const totalYield = useMemo(() => {
        if (!recipe) return 0;
        const base = Number(recipe.yields_qty || 0) * Number(batchMult || 0);
        const adj = Number(recipe.yield_adjustment_pct || 0);
        return base * (1 + adj / 100);
    }, [recipe, batchMult]);

    const totalPacked = useMemo(
        () => outputs.reduce((s, o) => s + (Number(o.container_capacity || 0) * Number(o.quantity_packed || 0)), 0),
        [outputs],
    );

    const overCapacity = totalPacked > totalYield * 1.001 && totalYield > 0;

    const addOutput = () => setOutputs((os) => [...os, { product_id: '', product_name: '', container_capacity: '', packaging_item_id: '', quantity_packed: '' }]);
    const removeOutput = (i) => setOutputs((os) => os.filter((_, idx) => idx !== i));
    const patchOutput = (i, patch) => setOutputs((os) => os.map((o, idx) => idx === i ? { ...o, ...patch } : o));

    async function submit() {
        if (!recipe) { toast.error('Select a production recipe.'); return; }
        if (!Number(batchMult) || Number(batchMult) <= 0) { toast.error('Batch multiplier must be > 0'); return; }
        if (!outputs.length) { toast.error('At least one packed output required.'); return; }
        for (const [i, o] of outputs.entries()) {
            if (!o.product_name?.trim()) return toast.error(`Row ${i + 1}: product name required`);
            if (!Number(o.container_capacity) || Number(o.container_capacity) <= 0) return toast.error(`Row ${i + 1}: container capacity required`);
            if (!Number(o.quantity_packed) || Number(o.quantity_packed) < 1) return toast.error(`Row ${i + 1}: quantity packed must be ≥ 1`);
        }
        if (overCapacity) return toast.error('Total packed capacity exceeds recipe yield.');
        setSubmitting(true);
        try {
            const res = await api.processBatch({
                recipe_id: recipe.id,
                batch_multiplier: Number(batchMult),
                overhead_cost: Number(overhead) || 0,
                packed_outputs: outputs.map((o) => ({
                    product_id: (o.product_id?.trim() || o.product_name.trim().toLowerCase().replace(/\s+/g, '-')),
                    product_name: o.product_name.trim(),
                    container_capacity: Number(o.container_capacity),
                    packaging_item_id: o.packaging_item_id || null,
                    quantity_packed: Number(o.quantity_packed),
                })),
            });
            toast.success(`Production run created · ${res.lots.length} lot${res.lots.length === 1 ? '' : 's'} · ${fmtMoney(res.cost_summary.total_batch_cost)}`);
            onSubmitted(res);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === 'object' && detail.error === 'Insufficient stock') {
                const rows = (detail.missing || []).map((m) => `${m.name}: need ${m.need} ${m.uom}, have ${m.have}`).join('\n');
                toast.error(`Insufficient stock:\n${rows}`, { duration: 8000 });
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Production failed');
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="production-wizard">
                <DialogHeader>
                    <DialogTitle>New production run</DialogTitle>
                    <DialogDescription>
                        Pick a production recipe, set batch multiplier, and allocate the yield across packed containers.
                    </DialogDescription>
                </DialogHeader>

                {loadingRecipes ? (
                    <div className="space-y-2">
                        <Skeleton className="h-10" />
                        <Skeleton className="h-10" />
                    </div>
                ) : recipes.length === 0 ? (
                    <div className="p-6 text-center border rounded-md bg-amber-50 dark:bg-amber-950/20">
                        <AlertTriangle className="mx-auto h-6 w-6 text-amber-600" />
                        <div className="font-medium mt-2">No production recipes found</div>
                        <div className="text-xs text-muted-foreground max-w-md mx-auto mt-1">
                            Go to <b>Recipes → Prep recipes</b> and create a recipe with type
                            <span className="mx-1 px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">production</span>
                            (must have SKU code, yields quantity + UOM).
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Recipe selection */}
                        <div>
                            <Label className="text-xs mb-1.5 block">Production recipe</Label>
                            <Select value={recipeId} onValueChange={setRecipeId}>
                                <SelectTrigger data-testid="production-recipe-select">
                                    <SelectValue placeholder="Select recipe…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {recipes.map((r) => (
                                        <SelectItem key={r.id} value={r.id}>
                                            {r.sku_code || '—'} · Yields {r.yields_qty} {r.yields_uom}
                                            {r.yield_adjustment_pct ? ` (${r.yield_adjustment_pct > 0 ? '+' : ''}${r.yield_adjustment_pct}% adj)` : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {recipe && (
                            <>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <Label className="text-xs mb-1.5 block">Batch multiplier</Label>
                                        <Input
                                            type="number" step="0.1" min="0.1"
                                            value={batchMult}
                                            onChange={(e) => setBatchMult(e.target.value)}
                                            data-testid="production-batch-multiplier"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1.5 block">Overhead ₹ (optional)</Label>
                                        <Input
                                            type="number" step="0.01" min="0"
                                            value={overhead}
                                            onChange={(e) => setOverhead(e.target.value)}
                                            placeholder="Electricity, labour…"
                                            data-testid="production-overhead"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs mb-1.5 block">Total yield</Label>
                                        <div className="h-9 px-3 flex items-center border rounded-md bg-muted/40 text-sm font-mono tabular-nums">
                                            {totalYield.toFixed(2)} {recipe.yields_uom}
                                        </div>
                                    </div>
                                </div>

                                {/* Packed outputs */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs">Packed outputs</Label>
                                        <Button size="sm" variant="outline" onClick={addOutput} className="gap-1 h-7" data-testid="production-add-output">
                                            <Plus size={11} /> Add container
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {outputs.map((o, i) => (
                                            <div key={i} className="grid grid-cols-12 gap-2 items-start p-2 border rounded-md" data-testid={`production-output-${i}`}>
                                                <div className="col-span-4">
                                                    <Input
                                                        placeholder="Product name (e.g. 500ml Bottle)"
                                                        value={o.product_name}
                                                        onChange={(e) => patchOutput(i, { product_name: e.target.value })}
                                                        className="h-8 text-sm"
                                                        data-testid={`production-output-${i}-name`}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <Input
                                                        type="number" step="0.1" min="0"
                                                        placeholder={`Capacity (${recipe.yields_uom})`}
                                                        value={o.container_capacity}
                                                        onChange={(e) => patchOutput(i, { container_capacity: e.target.value })}
                                                        className="h-8 text-sm"
                                                        data-testid={`production-output-${i}-capacity`}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <Input
                                                        type="number" min="1"
                                                        placeholder="Qty packed"
                                                        value={o.quantity_packed}
                                                        onChange={(e) => patchOutput(i, { quantity_packed: e.target.value })}
                                                        className="h-8 text-sm"
                                                        data-testid={`production-output-${i}-qty`}
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <Select
                                                        value={o.packaging_item_id || 'NONE'}
                                                        onValueChange={(v) => patchOutput(i, { packaging_item_id: v === 'NONE' ? '' : v })}
                                                    >
                                                        <SelectTrigger className="h-8 text-sm" data-testid={`production-output-${i}-packaging`}>
                                                            <SelectValue placeholder="Packaging item (optional)" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="NONE">— No packaging —</SelectItem>
                                                            {rawMaterials.map((rm) => (
                                                                <SelectItem key={rm.id} value={rm.id}>
                                                                    {rm.display_name} ({rm.stock_uom})
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="col-span-1 flex justify-end">
                                                    {outputs.length > 1 && (
                                                        <Button
                                                            size="sm" variant="ghost"
                                                            onClick={() => removeOutput(i)}
                                                            className="h-8 w-8 p-0"
                                                            data-testid={`production-output-${i}-remove`}
                                                        >
                                                            <Trash2 size={13} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={`mt-3 p-3 rounded-md border text-sm ${overCapacity ? 'border-red-300 bg-red-50 dark:bg-red-950/20' : 'border-border bg-muted/40'}`} data-testid="production-capacity-summary">
                                        <div className="flex justify-between">
                                            <span>Total packed capacity:</span>
                                            <span className="font-mono tabular-nums font-semibold">
                                                {totalPacked.toFixed(2)} / {totalYield.toFixed(2)} {recipe.yields_uom}
                                            </span>
                                        </div>
                                        {overCapacity && (
                                            <div className="text-xs text-red-700 dark:text-red-400 flex items-center gap-1 mt-1">
                                                <AlertTriangle size={12} /> Packed capacity exceeds yield — reduce quantities or add batch multiplier.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="production-cancel">Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || !recipe || overCapacity || !outputs.length}
                        className="gap-1"
                        data-testid="production-submit"
                    >
                        {submitting ? 'Running…' : <><Play size={13} /> Run production</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ═══════════════════════════════════════════════════════════════
 * LOTS TAB
 * ═════════════════════════════════════════════════════════════ */
function LotsTab() {
    const [lots, setLots] = useState(null);
    const [statusFilter, setStatusFilter] = useState('active');
    const [scanBarcode, setScanBarcode] = useState('');
    const [scanResult, setScanResult] = useState(null);
    const [reprintLot, setReprintLot] = useState(null);

    const load = useCallback(async () => {
        setLots(null);
        try {
            const params = statusFilter === 'all' ? {} : { status: statusFilter };
            const r = await api.listFinishedGoodsLots(params);
            setLots(r.items || []);
        } catch { setLots([]); }
    }, [statusFilter]);
    useEffect(() => { load(); }, [load]);

    async function doScan() {
        if (!scanBarcode.trim()) return;
        try {
            const r = await api.scanLot(scanBarcode.trim());
            setScanResult(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Lot not found');
            setScanResult(null);
        }
    }

    return (
        <div className="space-y-4">
            {/* Header row */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-40 h-9" data-testid="lots-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">Active only</SelectItem>
                            <SelectItem value="depleted">Depleted</SelectItem>
                            <SelectItem value="all">All lots</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={load} className="gap-1" data-testid="lots-refresh">
                        <RefreshCw size={13} />
                    </Button>
                </div>

                {/* Scan bar */}
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <QrCode size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Scan / enter batch #"
                            value={scanBarcode}
                            onChange={(e) => setScanBarcode(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') doScan(); }}
                            className="pl-8 h-9 w-56"
                            data-testid="lots-scan-input"
                        />
                    </div>
                    <Button size="sm" onClick={doScan} className="gap-1" data-testid="lots-scan-btn">
                        Look up
                    </Button>
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {lots === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : lots.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                            <PackageIcon size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No finished goods lots</div>
                        <div className="text-sm text-muted-foreground max-w-md mx-auto">
                            Run a production batch to create lots. Each lot is a traceable, barcoded
                            batch of finished goods.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Batch #</th>
                                    <th className="text-left px-4 py-2 font-medium">Product</th>
                                    <th className="text-right px-4 py-2 font-medium">Qty</th>
                                    <th className="text-right px-4 py-2 font-medium">Unit cost</th>
                                    <th className="text-left px-4 py-2 font-medium">MFG</th>
                                    <th className="text-left px-4 py-2 font-medium">EXP</th>
                                    <th className="text-left px-4 py-2 font-medium">Status</th>
                                    <th className="text-right px-4 py-2 font-medium">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lots.map((l, i) => (
                                    <LotRow key={l.id} lot={l} idx={i} onReprint={() => setReprintLot(l)} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {scanResult && (
                <Dialog open onOpenChange={(v) => { if (!v) setScanResult(null); }}>
                    <DialogContent data-testid="scan-result-dialog">
                        <DialogHeader>
                            <DialogTitle>Lot: {scanResult.lot.batch_number}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">Product</span><span className="font-medium">{scanResult.lot.product_name}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Current qty</span><span className="font-mono tabular-nums">{scanResult.lot.current_quantity} / {scanResult.lot.initial_quantity}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Unit cost</span><span className="font-mono tabular-nums">{fmtMoney(scanResult.lot.unit_cost_per_container)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">MFG · EXP</span><span className="font-mono">{scanResult.lot.manufacture_date} → {scanResult.lot.expiry_date}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={scanResult.lot.status} warning={scanResult.expiry_warning} /></div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {reprintLot && (
                <ReprintDialog lot={reprintLot} onClose={() => setReprintLot(null)} />
            )}
        </div>
    );
}


function LotRow({ lot, idx, onReprint }) {
    const expiryClass = (() => {
        if (!lot.expiry_date) return '';
        const now = new Date();
        const exp = new Date(lot.expiry_date + 'T00:00:00Z');
        const daysLeft = (exp - now) / (1000 * 60 * 60 * 24);
        if (lot.expiry_warning === 'expired' || daysLeft < 0) return 'text-red-700 dark:text-red-400 font-semibold';
        if (daysLeft <= 7) return 'text-red-600 dark:text-red-400 font-semibold';
        if (daysLeft <= 14) return 'text-amber-600 dark:text-amber-400 font-semibold';
        return 'text-green-700 dark:text-green-400';
    })();

    return (
        <tr className="border-t border-border hover:bg-muted/30" data-testid={`lot-row-${idx}`}>
            <td className="px-4 py-2 font-mono text-xs">{lot.batch_number}</td>
            <td className="px-4 py-2">
                <div className="font-medium">{lot.product_name}</div>
                <div className="text-[10px] uppercase text-muted-foreground">
                    {lot.container_capacity} {lot.yields_uom} · SKU {lot.sku_code}
                </div>
            </td>
            <td className="px-4 py-2 text-right tabular-nums font-mono">
                {lot.current_quantity} / {lot.initial_quantity}
            </td>
            <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(lot.unit_cost_per_container)}</td>
            <td className="px-4 py-2 text-xs text-muted-foreground">{lot.manufacture_date}</td>
            <td className={`px-4 py-2 text-xs ${expiryClass}`}>{lot.expiry_date}</td>
            <td className="px-4 py-2">
                <StatusBadge status={lot.status} warning={lot.expiry_warning} />
            </td>
            <td className="px-4 py-2 text-right">
                <Button variant="ghost" size="sm" className="gap-1 h-7" onClick={onReprint} data-testid={`lot-reprint-${idx}`}>
                    <Printer size={12} /> Label
                </Button>
            </td>
        </tr>
    );
}

function StatusBadge({ status, warning }) {
    if (warning === 'expired') return <Badge variant="destructive" className="gap-1"><AlertTriangle size={10} /> Expired</Badge>;
    if (warning === 'near_expiry') return <Badge className="bg-amber-500 hover:bg-amber-500 text-white gap-1"><AlertTriangle size={10} /> Near expiry</Badge>;
    if (status === 'active') return <Badge className="bg-green-600 hover:bg-green-600 text-white gap-1"><CircleCheck size={10} /> Active</Badge>;
    if (status === 'depleted') return <Badge variant="secondary">Depleted</Badge>;
    return <Badge variant="outline">{status}</Badge>;
}


function LabelPreviewDialog({ result, onClose }) {
    // result: { run, lots, labels: [{batch_number, zpl}], cost_summary }
    const [selected, setSelected] = useState(0);
    const current = result.labels?.[selected];

    function downloadZpl(single = false) {
        const content = single && current
            ? current.zpl
            : (result.labels || []).map((l) => l.zpl).join('\n');
        const filename = single && current
            ? `${current.batch_number}.zpl`
            : `production-${result.run.id.slice(0, 8)}-labels.zpl`;
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="production-result-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CircleCheck className="text-green-600" size={18} />
                        Production run complete
                    </DialogTitle>
                    <DialogDescription>
                        {result.lots.length} lot{result.lots.length === 1 ? '' : 's'} created ·
                        Batch total: <b>{fmtMoney(result.cost_summary.total_batch_cost)}</b> ·
                        Cost per {result.run.yields_uom}: <b>{fmtMoney(result.cost_summary.cost_per_yield_unit)}</b>
                    </DialogDescription>
                </DialogHeader>

                {/* Cost breakdown */}
                <div className="grid grid-cols-3 gap-2 text-sm">
                    <CostCell label="Raw materials" value={result.cost_summary.total_rm_cost} />
                    <CostCell label="Packaging" value={result.cost_summary.total_packaging_cost} />
                    <CostCell label="Overhead" value={result.cost_summary.overhead} />
                </div>

                {/* Lot chooser */}
                <div className="flex flex-wrap gap-1 border-t pt-3">
                    {(result.labels || []).map((l, i) => (
                        <Button
                            key={l.batch_number}
                            size="sm"
                            variant={i === selected ? 'default' : 'outline'}
                            onClick={() => setSelected(i)}
                            className="font-mono text-[11px] h-7"
                            data-testid={`label-tab-${i}`}
                        >
                            {l.batch_number}
                        </Button>
                    ))}
                </div>

                {/* ZPL preview */}
                {current && (
                    <div className="space-y-2">
                        <ZplPreview zpl={current.zpl} lot={result.lots[selected]} />
                        <pre className="p-3 bg-muted rounded-md text-[10px] font-mono max-h-40 overflow-auto">{current.zpl}</pre>
                    </div>
                )}

                <DialogFooter className="flex-wrap gap-2">
                    <Button variant="outline" onClick={() => downloadZpl(true)} className="gap-1" data-testid="production-download-one-zpl">
                        <FileDown size={13} /> Download this ZPL
                    </Button>
                    <Button onClick={() => downloadZpl(false)} className="gap-1" data-testid="production-download-all-zpl">
                        <FileDown size={13} /> Download all ({(result.labels || []).length})
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ReprintDialog({ lot, onClose }) {
    // We don't have the ZPL on hand — reconstruct via /scan endpoint (server-side truth).
    // But scan doesn't return ZPL. So we render a client-side approximation for the
    // label and offer plain text. For an exact ZPL re-print, we'd need a dedicated
    // endpoint — for now, offer a "reconstruct-inline" download that mirrors the
    // production-time payload.
    const zplReconstructed = (() => {
        // Simple, exact mirror of backend generate_zpl_label
        const veg = lot.is_veg ? '[VEG]' : '[NON-VEG]';
        return (
            "^XA" +
            "^PW812^LL406^LH0,0" +
            `^FO20,20^A0N,44,44^FD${(lot.product_name || 'PRODUCT').slice(0, 32)}^FS` +
            `^FO20,80^A0N,28,28^FD${(lot.product_name || '').slice(0, 40)}^FS` +
            `^FO20,120^A0N,22,22^FDNet: ${lot.container_capacity} ${lot.yields_uom}^FS` +
            `^FO20,155^A0N,20,20^FDBatch: ${lot.batch_number}^FS` +
            `^FO20,185^A0N,20,20^FDMFG: ${lot.manufacture_date}  EXP: ${lot.expiry_date}^FS` +
            `^FO20,220^BY2,3,60^BCN,60,Y,N,N^FD${lot.batch_number}^FS` +
            `^FO20,320^A0N,18,18^FDFSSAI: —^FS` +
            `^FO20,345^A0N,18,18^FDStore as per SOP  ${veg}^FS` +
            `^FO600,20^A0N,20,20^FD${lot.sku_code}^FS` +
            "^XZ"
        );
    })();

    function download() {
        const blob = new Blob([zplReconstructed], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${lot.batch_number}.zpl`;
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="reprint-dialog">
                <DialogHeader>
                    <DialogTitle>Reprint label · {lot.batch_number}</DialogTitle>
                    <DialogDescription>
                        Preview + download ZPL for this lot. Paste the ZPL into your Zebra printer utility.
                    </DialogDescription>
                </DialogHeader>
                <ZplPreview zpl={zplReconstructed} lot={lot} />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    <Button onClick={download} className="gap-1"><FileDown size={13} /> Download ZPL</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/**
 * ZplPreview — quick client-side visual approximation of the ZPL label.
 * Not pixel-accurate to the printer, but shows layout at a glance.
 */
function ZplPreview({ zpl, lot }) {
    return (
        <div className="border-2 border-dashed border-border rounded-md p-4 bg-white text-black w-full aspect-[2/1] flex flex-col justify-between" data-testid="zpl-preview">
            <div>
                <div className="text-xl font-black uppercase">{lot.product_name?.slice(0, 32) || 'PRODUCT'}</div>
                <div className="text-sm text-neutral-600">Net: {lot.container_capacity} {lot.yields_uom}</div>
            </div>
            <div className="text-[10px] text-neutral-700 font-mono">
                Batch: {lot.batch_number}
                <br />MFG: {lot.manufacture_date} · EXP: {lot.expiry_date}
                <br />SKU: {lot.sku_code} · {lot.is_veg ? '[VEG]' : '[NON-VEG]'}
            </div>
            {/* Fake barcode bars */}
            <div className="flex gap-[1px] h-6 items-end">
                {Array.from(lot.batch_number).slice(0, 40).map((c, i) => (
                    <div
                        key={i}
                        className="bg-black"
                        style={{ width: `${(c.charCodeAt(0) % 3) + 1}px`, height: `${((c.charCodeAt(0) % 5) + 3) * 3}px` }}
                    />
                ))}
            </div>
        </div>
    );
}


function CostCell({ label, value }) {
    return (
        <div className="p-2 border rounded-md bg-muted/30">
            <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
            <div className="font-mono tabular-nums font-semibold">{fmtMoney(value)}</div>
        </div>
    );
}
